import { useCallback } from "react";
import { updateResource } from "../../common/ApiUtils";
import { REST_AEROTOW_URL, TimeType, PilotType, DetailType, ActiveSelect } from "../../common/constants";
import { convTime } from "../../common/TimeUtils";

/**
 * Custom hook for managing tow updates
 */
export const useTowManagement = (flyDay, apiErrorHandler, setEditTow, setNavigationType, setLastPositionY, handleSelectHome, setActive, setFlyDay) => {
  const updateTowResource = useCallback(async (tow) => {
    const response = await updateResource(REST_AEROTOW_URL, tow);
    if (response && response.error) {
      apiErrorHandler(response);
      return false;
    }
    return true;
  }, [apiErrorHandler]);

  const timeChange = useCallback(async (id, type, value) => {
    if (!type || !flyDay?.tows) return;

    const tow = { ...flyDay.tows.find(item => item.id === id) };
    if (!tow) return;

    if (type === TimeType.START_TIME) {
      if (!value) return;
      tow.start = convTime(value);
    } else if (type === TimeType.LANDING_GLIDER_TIME) {
      tow.gliderLanding = value ? convTime(value) : null;
    } else if (type === TimeType.LANDING_TOWING_TIME) {
      tow.towingPlaneLanding = value ? convTime(value) : null;
    }

    const success = await updateTowResource(tow);
    if (success && setFlyDay) {
      setFlyDay((prev) => {
        if (!prev?.tows) return prev;
        return { ...prev, tows: prev.tows.map((t) => (t.id === tow.id ? tow : t)) };
      });
    }
    return success;
  }, [flyDay, updateTowResource, setFlyDay]);

  const changeTow = useCallback(async (id, type, value) => {
    if (!flyDay?.tows) return false;

    const tow = { ...flyDay.tows.find(item => item.id === id) };
    if (!tow) return false;

    if (type === PilotType.TOWING_PILOT) {
      if (!value) return false;
      tow.lifter = value;
    } else if (type === PilotType.GLIDER_FIRST) {
      if (!value) return false;
      tow.first = value;
    } else if (type === PilotType.GLIDER_SECOND) {
      if (!value) return false;
      tow.second = value;
    } else if (type === DetailType.NOTE) {
      tow.note = value;
    }

    const success = await updateTowResource(tow);
    if (success && setFlyDay) {
      setFlyDay((prev) => {
        if (!prev?.tows) return prev;
        return { ...prev, tows: prev.tows.map((t) => (t.id === tow.id ? tow : t)) };
      });
    }
    return success;
  }, [flyDay, updateTowResource, setFlyDay]);

  const gliderTaskChange = useCallback(async (tow, scrollPosition) => {
    // Store scroll position if provided (captured at click time from DOM)
    // 0 is a valid scroll position (top of page), so accept it
    if (scrollPosition !== undefined && scrollPosition !== null && scrollPosition >= 0) {
      setLastPositionY(scrollPosition);
    }
    
    // Navigate to task selection
    setEditTow(tow);
    setNavigationType(ActiveSelect.GLIDER_TASK_DETAIL);
  }, [setEditTow, setNavigationType, setLastPositionY]);

  const towTaskChange = useCallback(async (tow, scrollPosition) => {
    // Store scroll position if provided (captured at click time from DOM)
    // 0 is a valid scroll position (top of page), so accept it
    if (scrollPosition !== undefined && scrollPosition !== null && scrollPosition >= 0) {
      setLastPositionY(scrollPosition);
    }
    
    // Navigate to task selection
    setEditTow(tow);
    setNavigationType(ActiveSelect.TOW_TASK_DETAIL);
  }, [setEditTow, setNavigationType, setLastPositionY]);

  const setGliderTask = useCallback(async (task, editTow) => {
    if (!editTow) return;

    const updatedTow = { ...editTow, gliderTask: task };
    const success = await updateTowResource(updatedTow);
    if (success) {
      if (setFlyDay) {
        setFlyDay((prev) => {
          if (!prev?.tows) return prev;
          return { ...prev, tows: prev.tows.map((t) => (t.id === updatedTow.id ? updatedTow : t)) };
        });
      }
      // Restore scroll position after returning home
      handleSelectHome();
      // Reset scroll position after it's been used (give enough time for scroll to complete)
      setTimeout(() => {
        setLastPositionY(0);
      }, 1000);
    }
  }, [updateTowResource, handleSelectHome, setLastPositionY, setFlyDay]);

  const setTowTask = useCallback(async (task, editTow) => {
    if (editTow) {
      const updatedTow = { ...editTow, towTask: task };
      await setActive('towTask', task);
      const success = await updateTowResource(updatedTow);
      if (success) {
        if (setFlyDay) {
          setFlyDay((prev) => {
            if (!prev?.tows) return prev;
            return { ...prev, tows: prev.tows.map((t) => (t.id === updatedTow.id ? updatedTow : t)) };
          });
        }
        // Restore scroll position after returning home
        handleSelectHome();
        // Reset scroll position after it's been used (give enough time for scroll to complete)
        setTimeout(() => {
          setLastPositionY(0);
        }, 1000);
      }
    } else {
      await setActive('towTask', task);
      handleSelectHome();
    }
  }, [setActive, updateTowResource, handleSelectHome, setLastPositionY, setFlyDay]);

  return {
    timeChange,
    changeTow,
    gliderTaskChange,
    towTaskChange,
    setGliderTask,
    setTowTask
  };
};

